import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import toast from "react-hot-toast";
import Box from '../../components/Box';
import { MenuComponent } from '../../_metronic/assets/ts/components';
import { KTCard } from '../../_metronic/helpers/components/KTCard';
import { KTCardBody } from '../../_metronic/helpers/components/KTCardBody';
import Content from '../../_metronic/layout/components/content/Content';
import { PageTitle } from '../../_metronic/layout/core';
import { ToolbarWrapper } from '../../_metronic/layout/components/toolbar';
import { storeActivityIndicator } from '../../redux/reducers/authSlice';
import { RootState } from '../../redux/store';
import RefreshButton from '../../components/RefreshButton';
import ColumnChooser from '../../components/ColumnChooser';
import TableConstantActions from '../../components/TableConstantActions';
import CustomDatePicker from '../../components/CustomDatePicker';
import CustomTextInput from '../../components/CustomTextInput';
import CustomSingleSelect, { CustomSingleSelectModel } from '../../components/CustomSingleSelect';
import { ColumnChooserModel } from '../../models/common/ColumnChooserModel';
import { ColumnFilterModel } from '../../models/common/ColumnFilterModel';
import { kolonlariBirlestir } from '../../components/ColumnFilter/kolonBirlestir';
import ColumnFilterRow from '../../components/ColumnFilter/ColumnFilterRow';
import ColumnFilterChips from '../../components/ColumnFilter/ColumnFilterChips';
import { ColumnFilterDefinitions, foldText } from '../../components/ColumnFilter/ColumnFilterTypes';
import { useSortableColumns } from '../../helpers/useSortableColumns';
import { OZEL_GUN_TURLERI, OzelGunItem, OzelGunService } from '../../services/OzelGunService';

const GUN_ADLARI = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

const gunAdi = (tarih: string) => GUN_ADLARI[moment(tarih.substring(0, 10), 'YYYY-MM-DD').day()] ?? '';

const etkisi = (x: OzelGunItem) => x.tur == 1
    ? 'Okutulan sürenin tamamı ek mesai (resmi tatil çarpanı)'
    : `Çalışma bitişi ${x.yarimGunBitis ?? ''}; sonrası ek mesai`;

const turSecenegi = (deger: number): CustomSingleSelectModel => ({ label: OZEL_GUN_TURLERI.find(x => x.value == deger)?.label ?? '', value: String(deger) });

/** Ekranda süzme / sıralama için satırın kolon değeri. */
const kolonDegeri = (x: OzelGunItem, kolon: string): string => {
    switch (kolon) {
        case 'tarih': return x.tarih;
        case 'gun': return String(moment(x.tarih.substring(0, 10), 'YYYY-MM-DD').isoWeekday());
        case 'ad': return x.ad ?? '';
        case 'turAdi': return String(x.tur);
        case 'yarimGunBitis': return x.yarimGunBitis ?? '';
        case 'etkisi': return etkisi(x);
        default: return '';
    }
}

interface EkranFiltresi {
    sortColumn?: string
    sortDirection?: string
    columnFilters: ColumnFilterModel[]
}

/**
 * Özel Günler (İnsan Kaynakları > Tanımlamalar, QR Mesai Faz 1b, 11.10.2026).
 * Yıllık kısa liste olduğu için tek ekran: liste + üstte açılan ekleme / güncelleme formu (Sabit Gelir grid kalıbı).
 * Liste yıl bazında tek seferde gelir; kolon filtresi ve sıralama ekranda uygulanır. Excel ucu sözleşmede yok.
 */
const OzelGunList = () => {
    const dispatch = useDispatch();
    const auth = useSelector((state: RootState) => state.auth)
    const pageName = "İnsan Kaynakları > Tanımlamalar > Özel Günler"
    const pageVersion = "1";
    const menu = auth.user.user?.data.authorizedMenus.find(x => x.menuName == pageName);
    const formRef = useRef<any>(null);

    const [yil, setYil] = useState<number>(moment().year());
    const [liste, setListe] = useState<OzelGunItem[]>([]);
    const [filter, setFilter] = useState<EkranFiltresi>({ sortColumn: 'tarih', sortDirection: 'asc', columnFilters: [] });

    // Ekleme / güncelleme formu
    const [isOpen, setIsOpen] = useState(false);
    const [duzenlenenId, setDuzenlenenId] = useState<string | undefined>(undefined);
    const [tarih, setTarih] = useState<string | undefined>(undefined);
    const [ad, setAd] = useState<string | undefined>(undefined);
    const [selectedTur, setSelectedTur] = useState<CustomSingleSelectModel | undefined>(turSecenegi(1));
    const [yarimGunBitis, setYarimGunBitis] = useState<string>('13:00');

    const getColumns = () => {
        var str = localStorage.getItem(pageName + "-" + pageVersion + "-filters");

        var list: ColumnChooserModel[] = [
            { title: 'Tarih', name: 'tarih', order: 0, isVisible: true },
            { title: 'Gün', name: 'gun', order: 0, isVisible: true },
            { title: 'Ad', name: 'ad', order: 0, isVisible: true },
            { title: 'Tür', name: 'turAdi', order: 0, isVisible: true },
            { title: 'Yarım Gün Bitişi', name: 'yarimGunBitis', order: 0, isVisible: true },
            { title: 'Etkisi', name: 'etkisi', order: 0, isVisible: true },
        ];

        for (var i = 0; i < list.length; i++)
            list[i].order = i + 1;

        return kolonlariBirlestir(list, str);
    }

    const [columns, setColumns] = useState<ColumnChooserModel[]>(getColumns());

    useEffect(() => {
        setTimeout(() => { MenuComponent.reinitialization() }, 1000)
    }, [])

    useEffect(() => {
        getData();
    }, [yil])

    const getData = async () => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await OzelGunService.getList(yil);

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            setListe((response.data ?? []).map(x => ({ ...x, isSelected: false })));
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    // ---------------------------------------------------------------- Ekranda süzme / sıralama

    const gorunen = liste.filter(x => (filter.columnFilters ?? []).every(f => {
        if (f.values && f.values.length > 0)
            return f.values.includes(kolonDegeri(x, f.column));

        if (f.text && f.text.trim())
            return foldText(kolonDegeri(x, f.column)).includes(foldText(f.text.trim()));

        if (f.from || f.to) {
            var d = kolonDegeri(x, f.column);
            return (!f.from || d >= f.from) && (!f.to || d <= f.to);
        }

        return true;
    })).sort((a, b) => {
        if (!filter.sortColumn)
            return 0;

        var r = kolonDegeri(a, filter.sortColumn).localeCompare(kolonDegeri(b, filter.sortColumn), 'tr');
        return filter.sortDirection == 'desc' ? -r : r;
    });

    const sortableColumns = ['tarih', 'gun', 'ad', 'turAdi', 'yarimGunBitis'];

    const { thProps, sortArrow } = useSortableColumns(sortableColumns, filter, (f?: EkranFiltresi) => { if (f) setFilter(f); });

    const columnFilterDefinitions: ColumnFilterDefinitions = {
        tarih: { type: 'date' },
        ad: { type: 'text' },
        turAdi: { type: 'select', allowEmpty: false, loadOptions: () => Promise.resolve(OZEL_GUN_TURLERI.map(x => ({ value: String(x.value), label: x.label }))) },
        yarimGunBitis: { type: 'text' },
    };

    const changeColumnFilters = (columnFilters: ColumnFilterModel[]) => setFilter({ ...filter, columnFilters });

    // ---------------------------------------------------------------- Seçim

    const setItemSelected = (_id: string) => {
        setListe(liste.map(x => x.ozelGunId == _id ? { ...x, isSelected: !x.isSelected } : x));
    }

    const selectAll = () => {
        var ids = gorunen.map(x => x.ozelGunId);
        var isAllSelected = gorunen.length > 0 && gorunen.every(x => x.isSelected);

        setListe(liste.map(x => ids.includes(x.ozelGunId) ? { ...x, isSelected: !isAllSelected } : x));
    }

    const secililer = liste.filter(x => x.isSelected);

    // ---------------------------------------------------------------- Form

    const scrollTo = () => {
        setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }

    const formuTemizle = () => {
        setDuzenlenenId(undefined);
        setTarih(undefined);
        setAd(undefined);
        setSelectedTur(turSecenegi(1));
        setYarimGunBitis('13:00');
    }

    const yeniEkle = () => {
        formuTemizle();
        setIsOpen(true);
        scrollTo();
    }

    const iptalEt = () => {
        formuTemizle();
        setIsOpen(false);
    }

    const guncelle = () => {
        var o = secililer[0];

        if (!o)
            return;

        setDuzenlenenId(o.ozelGunId);
        setTarih(o.tarih.substring(0, 10));
        setAd(o.ad);
        setSelectedTur(turSecenegi(o.tur));
        setYarimGunBitis(o.yarimGunBitis ?? '13:00');
        setIsOpen(true);
        scrollTo();
    }

    const kaydet = async () => {
        try {
            var tur = Number(selectedTur?.value ?? 1);

            if (!tarih) {
                toast.error("Tarih alanı zorunludur.");
                return;
            }

            if (!ad || ad.trim() == '') {
                toast.error("Ad alanı zorunludur.");
                return;
            }

            if (tur == 2 && !yarimGunBitis) {
                toast.error("Yarım günde çalışma bitişi zorunludur.");
                return;
            }

            if (liste.some(x => x.tarih.substring(0, 10) == tarih && x.ozelGunId != duzenlenenId)) {
                toast.error("Bu tarihte bir özel gün zaten var.");
                return;
            }

            dispatch(storeActivityIndicator(true));

            var response = await OzelGunService.kaydet({
                ozelGunId: duzenlenenId ?? null,
                tarih: tarih,
                ad: ad.trim(),
                tur: tur,
                yarimGunBitis: tur == 2 ? yarimGunBitis : null
            });

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                dispatch(storeActivityIndicator(false));
                return;
            }

            toast.success("Özel gün kaydedildi. O tarihteki mesai kayıtları yeniden hesaplandı.");

            formuTemizle();
            setIsOpen(false);

            // Başka yıla kaydedildiyse o yıl açılır (yıl değişince liste kendiliğinden yenilenir).
            var kayitYili = Number(tarih.substring(0, 4));

            if (kayitYili != yil)
                setYil(kayitYili);
            else
                await getData();
        } catch (error: any) {
            toast.error(error);
            dispatch(storeActivityIndicator(false));
        }
    }

    const sil = async () => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await OzelGunService.sil(secililer.map(x => x.ozelGunId));

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                dispatch(storeActivityIndicator(false));
                return;
            }

            toast.success("Seçilen özel günler silindi.");

            iptalEt();
            await getData();
        } catch (error: any) {
            toast.error(error);
            dispatch(storeActivityIndicator(false));
        }
    }

    const resmiTatilleriYukle = async () => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await OzelGunService.resmiTatilleriYukle(yil);

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                dispatch(storeActivityIndicator(false));
                return;
            }

            var sayi = Number(response.data ?? 0);

            if (sayi > 0)
                toast.success(`${yil} için ${sayi} resmi tatil eklendi. Dini bayramları elle ekleyin.`);
            else
                toast(`${yil} için eklenecek yeni resmi tatil yok (hepsi zaten tanımlı).`);

            await getData();
        } catch (error: any) {
            toast.error(error);
            dispatch(storeActivityIndicator(false));
        }
    }

    // ---------------------------------------------------------------- Hücreler

    const hucre = (item: OzelGunItem, column: string) => {
        var icerik: React.ReactNode;

        if (column == 'tarih')
            icerik = item.tarihStr;
        else if (column == 'gun')
            icerik = gunAdi(item.tarih);
        else if (column == 'ad')
            icerik = item.ad;
        else if (column == 'turAdi')
            icerik = <span className={'badge ebbe-status-badge ' + (item.tur == 1 ? 'badge-light-danger' : 'badge-light-warning')}>{item.turAdi}</span>;
        else if (column == 'yarimGunBitis')
            icerik = item.tur == 2 ? item.yarimGunBitis : '';
        else if (column == 'etkisi')
            icerik = etkisi(item);

        return <td key={column} onClick={() => setItemSelected(item.ozelGunId)} onDoubleClick={() => { setListe(liste.map(x => ({ ...x, isSelected: x.ozelGunId == item.ozelGunId }))); guncelleById(item)(); }}>{icerik}</td>;
    }

    // Çift tıklama: satırı seçip formda açar.
    const guncelleById = (o: OzelGunItem) => () => {
        if (!menu?.canUpdate)
            return;

        setDuzenlenenId(o.ozelGunId);
        setTarih(o.tarih.substring(0, 10));
        setAd(o.ad);
        setSelectedTur(turSecenegi(o.tur));
        setYarimGunBitis(o.yarimGunBitis ?? '13:00');
        setIsOpen(true);
        scrollTo();
    }

    const gorunenKolonlar = columns.filter(x => x.isVisible).sort((a, b) => a.order - b.order);
    const buYil = moment().year();
    const yillar = [buYil - 2, buYil - 1, buYil, buYil + 1, buYil + 2];

    return (
        <React.Fragment>
            <ToolbarWrapper filters={<React.Fragment>
                <RefreshButton onClick={() => getData()} />

                <div className='m-0'>
                    <select
                        className='form-select form-select-sm form-select-solid'
                        style={{ width: 100 }}
                        title='Yıl'
                        value={yil}
                        onChange={(e) => setYil(Number(e.target.value))}
                    >
                        {(yillar.includes(yil) ? yillar : [...yillar, yil].sort()).map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>

                <TableConstantActions
                    deleteFunction={sil}
                    onSearchSubmit={(e) => e.preventDefault()}
                    pageName={pageName}
                    hideSearch={true}
                    searchTerm=''
                    selectedCount={secililer.length}
                    setSearchTerm={() => { }}
                    updateFunction={guncelle}
                />

                <ColumnChooser
                    columns={columns}
                    getColumns={getColumns}
                    pageName={pageName}
                    pageVersion={pageVersion}
                    setColumns={setColumns}
                />

                {
                    menu?.canInsert && (
                        <div className='m-0 me-2'>
                            <a onClick={resmiTatilleriYukle} className='btn btn-sm fw-bold btn-light-primary' title={`${yil} yılının sabit tarihli resmi tatillerini ekler`}>
                                <i className='fa fa-calendar-plus fs-6 me-1' />
                                Resmi tatilleri yükle
                            </a>
                        </div>
                    )
                }

                {
                    menu?.canInsert && (
                        <div className='m-0'>
                            <a onClick={yeniEkle} className='btn btn-sm fw-bold btn-primary'>
                                Yeni Özel Gün Ekle
                            </a>
                        </div>
                    )
                }
            </React.Fragment>} />

            <PageTitle breadcrumbs={[{
                title: pageName.split('>').slice(0, pageName.split('>').length - 1).join(' > ') + ' > ',
                isActive: false,
                path: ''
            }]}>{(pageName.split('>')[pageName.split('>').length - 1])}</PageTitle>

            <Content>
                {
                    isOpen && (
                        <div ref={formRef}>
                            <KTCard className='ebbe-card mb-3'>
                                <KTCardBody>
                                    <h4 className="mb-10 text-black">{duzenlenenId ? 'Özel Günü Güncelle' : 'Yeni Özel Gün'}</h4>

                                    <div className="row">
                                        <div className="col-md-3">
                                            <CustomDatePicker labelText="Tarih" isLabelRequired value={tarih} setValue={setTarih} />
                                        </div>

                                        <div className="col-md-3">
                                            <CustomTextInput labelText="Ad" isLabelRequired value={ad} setValue={setAd} />
                                        </div>

                                        <div className="col-md-3">
                                            <CustomSingleSelect
                                                labelText="Tür"
                                                isLabelRequired
                                                selectedValue={selectedTur}
                                                onChange={(v) => setSelectedTur(v ?? turSecenegi(1))}
                                                onMenuOpen={() => Promise.resolve({ isSuccess: true, data: OZEL_GUN_TURLERI.map(x => ({ label: x.label, value: String(x.value) })) })}
                                                labelName='label'
                                                valueName='value'
                                            />
                                        </div>

                                        <div className="col-md-3">
                                            {
                                                selectedTur?.value == '2' && (
                                                    <React.Fragment>
                                                        <label className="form-label required">Yarım gün bitişi</label>
                                                        <input
                                                            type="time"
                                                            className="form-control form-control-solid"
                                                            style={{ border: '1px solid #F1F1F4' }}
                                                            value={yarimGunBitis}
                                                            onChange={(e) => setYarimGunBitis(e.target.value)}
                                                        />
                                                    </React.Fragment>
                                                )
                                            }
                                        </div>
                                    </div>

                                    <div className="text-muted fs-7 mt-5">
                                        Resmi tatilde okutulan sürenin tamamı ek mesaidir (resmi tatil çarpanı). Yarım günde o günün çalışma bitişi bu saattir; sonrası ek mesai.
                                        {' '}Kaydedince o tarihteki mesai kayıtları ve faturalanmamış siparişler yeniden hesaplanır.
                                    </div>

                                    <Box flexDirection="row" justifyContent="flex-end" className="mt-5">
                                        <Box mr="15px">
                                            <button className="btn btn-sm btn-light btn-active-light-primary" onClick={iptalEt}>İptal Et</button>
                                        </Box>
                                        <Box>
                                            <button className="btn btn-primary btn-sm" onClick={kaydet}>Kaydet</button>
                                        </Box>
                                    </Box>
                                </KTCardBody>
                            </KTCard>
                        </div>
                    )
                }

                <KTCard className='mb-3'>
                    <KTCardBody className='py-2'>
                        <ColumnFilterChips
                            columns={columns}
                            definitions={columnFilterDefinitions}
                            filters={filter.columnFilters}
                            onChange={changeColumnFilters}
                        />

                        <div className='table-responsive new-table-container'>
                            <table id='kt_table_users' className='table align-middle table-row-dashed dataTable no-footer ebbe-compact-table'>
                                <thead>
                                    <tr className='text-start text-muted fw-bolder text-uppercase'>
                                        <th style={{ width: 1 }}>
                                            <div className='d-flex'>
                                                <label className='form-check form-check-sm form-check-custom form-check-solid me-5'>
                                                    <input onChange={selectAll} className='form-check-input' type='checkbox' checked={gorunen.length > 0 && gorunen.every(x => x.isSelected)} />
                                                </label>
                                            </div>
                                        </th>

                                        {
                                            gorunenKolonlar.map(item =>
                                                <th key={item.name} {...thProps(item.name)}>
                                                    {item.title}
                                                    {sortArrow(item.name)}
                                                </th>
                                            )
                                        }
                                    </tr>

                                    <ColumnFilterRow
                                        columns={gorunenKolonlar}
                                        definitions={columnFilterDefinitions}
                                        filters={filter.columnFilters}
                                        onChange={changeColumnFilters}
                                        leadingCellCount={1}
                                    />
                                </thead>
                                <tbody className='text-gray-600 fw-bold'>
                                    {
                                        gorunen.length == 0 && (
                                            <tr>
                                                <td className="text-center" colSpan={99}>Kayıt bulunamadı.</td>
                                            </tr>
                                        )
                                    }
                                    {
                                        gorunen.map(item =>
                                            <tr key={item.ozelGunId} style={{ background: item.isSelected ? 'rgba(204, 204, 204, 0.25)' : undefined }}>
                                                <td style={{ width: 1 }}>
                                                    <div className='d-flex'>
                                                        <label className='form-check form-check-sm form-check-custom form-check-solid me-5'>
                                                            <input onChange={() => setItemSelected(item.ozelGunId)} className='form-check-input' type='checkbox' checked={item.isSelected ?? false} />
                                                        </label>
                                                    </div>
                                                </td>

                                                {gorunenKolonlar.map(c => hucre(item, c.name))}
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                        </div>

                        <div className="text-muted fs-7 py-3">
                            {gorunen.length} kayıt · "Resmi tatilleri yükle" sabit tarihli resmi tatilleri (1 Ocak, 23 Nisan, 1 Mayıs, 19 Mayıs, 15 Temmuz, 30 Ağustos, 29 Ekim) ve 28 Ekim yarım gününü ekler; dini bayramları elle ekleyin.
                        </div>
                    </KTCardBody>
                </KTCard>
            </Content>
        </React.Fragment>
    )
}

export default OzelGunList;
